import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Globe, RefreshCw, Search, Radio, Users, FileText, Handshake, BookOpen, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const KIND_CONFIG = {
  handshake: { icon: Handshake, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  manifest: { icon: Radio, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  library_index: { icon: BookOpen, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  document_ref: { icon: FileText, color: 'text-violet-400', bg: 'bg-violet-500/10' },
};

export default function FederationWire({ apiUrl }) {
  const [items, setItems] = useState([]);
  const [peers, setPeers] = useState([]);
  const [coreIdentity, setCoreIdentity] = useState(null);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterKind, setFilterKind] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [libRes, peersRes, identRes, bridgeRes] = await Promise.all([
        axios.get(`${apiUrl}/federation/upstream/library`).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/federation/upstream/peers`).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/federation/upstream/identity`).catch(() => ({ data: {} })),
        axios.get(`${apiUrl}/core-bridge/status`).catch(() => ({ data: {} })),
      ]);
      setItems(libRes.data?.items || []);
      setPeers(peersRes.data?.peers || []);
      setCoreIdentity(identRes.data);
      setBridgeStatus(bridgeRes.data);
    } catch (e) {
      console.error('Federation wire fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${apiUrl}/core-bridge/full-sync`);
      toast.success('Bridge sync initiated');
      setTimeout(fetchAll, 5000);
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { fetchAll(); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/federation/upstream/library/search`, { params: { q: searchQuery } });
      setItems(res.data?.items || res.data?.results || []);
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const filtered = filterKind ? items.filter(i => i.kind === filterKind) : items;

  const formatTime = (t) => {
    if (!t) return '';
    try { return new Date(t).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return t; }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 px-4 py-6 max-w-6xl mx-auto" data-testid="federation-wire">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Globe className="w-5 h-5 text-cyan-400" />
            <h1 className="text-xl font-bold text-white">Federation Wire</h1>
            {bridgeStatus?.core_reachable && (
              <span className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                Core Live
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Live view of the Federation relay at nextxus.online — {items.length} items on the wire
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleSync}
          disabled={syncing}
          className="gap-1.5 text-xs"
          data-testid="sync-button"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Push to Core
        </Button>
      </div>

      {/* Bridge Stats */}
      {bridgeStatus && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Delivered', value: bridgeStatus.delivered, color: 'text-emerald-400' },
            { label: 'Failed', value: bridgeStatus.failed, color: bridgeStatus.failed > 0 ? 'text-rose-400' : 'text-slate-500' },
            { label: 'On Wire', value: items.length, color: 'text-cyan-400' },
            { label: 'Peers', value: peers.length, color: 'text-amber-400' },
          ].map(s => (
            <div key={s.label} className="bg-slate-900/60 border border-slate-800/40 rounded-lg p-3 text-center">
              <div className={`text-lg font-bold ${s.color}`}>{s.value}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Peers */}
      {peers.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-300">Connected Peers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {peers.map((p, i) => (
              <div key={i} className="bg-slate-900/40 border border-slate-800/30 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-white">{p.source}</div>
                  <div className="text-[10px] text-slate-500">{p.transmissions} transmissions · Last: {formatTime(p.last_received_at)}</div>
                </div>
                <div className="flex gap-1 flex-wrap justify-end">
                  {(p.kinds || []).map(k => {
                    const cfg = KIND_CONFIG[k] || KIND_CONFIG.document_ref;
                    return <Badge key={k} className={`text-[9px] ${cfg.color} ${cfg.bg} border-0`}>{k}</Badge>;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            placeholder="Search the wire..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="pl-9 bg-slate-900/60 border-slate-800/40 text-sm h-9"
            data-testid="wire-search"
          />
        </div>
        {['handshake', 'manifest', 'library_index', 'document_ref'].map(k => {
          const cfg = KIND_CONFIG[k];
          const Icon = cfg.icon;
          return (
            <Button
              key={k}
              variant={filterKind === k ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterKind(filterKind === k ? null : k)}
              className={`gap-1 text-[10px] h-9 ${filterKind === k ? '' : cfg.color}`}
            >
              <Icon className="w-3 h-3" />
              <span className="hidden sm:inline">{k.replace('_', ' ')}</span>
            </Button>
          );
        })}
      </div>

      {/* Wire Items */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading wire...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-slate-600">
          <Globe className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No items on the wire{filterKind ? ` for kind: ${filterKind}` : ''}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((item, i) => {
            const cfg = KIND_CONFIG[item.kind] || KIND_CONFIG.document_ref;
            const Icon = cfg.icon;
            return (
              <div
                key={item.receipt_id || i}
                className="bg-slate-900/40 border border-slate-800/30 rounded-lg p-3 hover:border-slate-700/50 transition-colors"
                data-testid={`wire-item-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`${cfg.bg} rounded-lg p-2 mt-0.5 flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-[9px] ${cfg.color} ${cfg.bg} border-0`}>{item.kind}</Badge>
                      <span className="text-[10px] text-slate-600">{item.source}</span>
                      <span className="text-[10px] text-slate-600 ml-auto flex-shrink-0">{formatTime(item.received_at)}</span>
                    </div>
                    <p className="text-sm text-slate-200 leading-snug">{item.summary}</p>
                    {item.payload?.title && item.payload.title !== item.summary && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{item.payload.title}</p>
                    )}
                    {item.tags?.length > 0 && (
                      <div className="flex gap-1 mt-1.5 flex-wrap">
                        {item.tags.slice(0, 5).map(t => (
                          <span key={t} className="text-[9px] text-slate-600 bg-slate-800/40 px-1.5 py-0.5 rounded">{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Core Identity Footer */}
      {coreIdentity?.name && (
        <div className="mt-8 border-t border-slate-800/30 pt-4 text-center">
          <p className="text-[10px] text-slate-600">
            Relay: {coreIdentity.name} · {coreIdentity.domain} · Wire v{coreIdentity.wire_version || '1.0'}
          </p>
        </div>
      )}
    </div>
  );
}
